import React, { PropsWithChildren } from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { VStack } from '../vstack'
import { Pagination, PaginationVariant } from './Pagination'

export type PaginationListProps = {
    /** Zero-based index of the current page. */
    currentPage: number
    totalPages: number
    /** Max number of page cells to show before collapsing with an ellipsis. */
    maxButtons?: number
    onChange: (page: number) => void
    prevLabel: string
    nextLabel: string
    variant?: PaginationVariant
    /**
     * When true the content region scrolls internally (`flex-1` + `min-h-0` +
     * `overflow-y-auto`) so the pagination footer stays pinned. Requires a
     * bounded-height flex parent. Off by default — most lists rely on an
     * outer scroll container.
     */
    scrollable?: boolean
    className?: string
    testID?: string
}

/**
 * List layout + `Pagination` footer: a flex column that gives the content the
 * available space and pins the pagination below, shown only when there is more
 * than one page. Presentational + controlled — i18n labels and the page model
 * are mapped by the caller. Tenant-agnostic tokens, so AOS/AOIMS render alike.
 */
export const PaginationList: React.FC<PropsWithChildren<PaginationListProps>> = ({
    currentPage,
    totalPages,
    maxButtons,
    onChange,
    prevLabel,
    nextLabel,
    variant = 'dark',
    scrollable = false,
    className,
    testID,
    children,
}) => (
    <VStack testID={testID} className={twMerge('flex-1', className)}>
        <Box className={twMerge('flex-1', scrollable && 'min-h-0 overflow-y-auto')}>{children}</Box>
        {totalPages > 1 && (
            <Box className='mt-2'>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    maxButtons={maxButtons}
                    onChange={onChange}
                    prevLabel={prevLabel}
                    nextLabel={nextLabel}
                    variant={variant}
                />
            </Box>
        )}
    </VStack>
)

PaginationList.displayName = 'PaginationList'
